import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
} from '@mui/material';
import { Edit, Trash, ArrowLeftCircle, Calendar } from 'react-feather';
import { useParams, useNavigate } from 'react-router-dom';
import ScheduleForm from './ScheduleForm';
import api from '../network/ApiClient';

const ScheduleRoute = () => {
  const getTomorrowDate = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate());
    return tomorrow.toISOString().split('T')[0];
  };
  const { routeId } = useParams();
  const navigate = useNavigate();
  const [routeName, setRouteName] = useState('');
  const [rides, setRides] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const [editedRide, setEditedRide] = useState({});
  const [openPopup, setOpenPopup] = useState(false);
  const [selectedRideId, setSelectedRideId] = useState(null);
  const [actionType, setActionType] = useState('');

  // Two-phase delete: the first DELETE probes for active bookings. When the
  // backend reports bookings (deleted === false) we hold the pending delete
  // and ask the operator to confirm a force delete (cancel + refund).
  const [bookingsPopup, setBookingsPopup] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [bookingWarning, setBookingWarning] = useState('');

  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [showEditedDepart, setShowEditedDepart] = useState(false);

  const [newSchedule, setNewSchedule] = useState({
    start: getTomorrowDate(),
    end: '',
    depart: '',
    vehicle: '',
    driver: '',
    nth: 1,
    every: 0,
  });

  const [loading, setLoading] = useState(false);

  const normalizeTimeValue = (timeValue) => {
    if (!timeValue) return '';
    const trimmed = timeValue.trim();
    const ampmMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)$/i);
    if (ampmMatch) {
      const [, rawHours, minutes, seconds = '00', modifier] = ampmMatch;
      let hours = parseInt(rawHours, 10) % 12;
      if (modifier.toLowerCase() === 'pm') {
        hours += 12;
      }
      const paddedHours = String(hours).padStart(2, '0');
      return seconds ? `${paddedHours}:${minutes}:${seconds}` : `${paddedHours}:${minutes}`;
    }
    if (/^\d{1,2}:\d{2}:\d{2}$/.test(trimmed)) {
      const [hours, minutes, seconds] = trimmed.split(':');
      return `${String(parseInt(hours, 10)).padStart(2, '0')}:${minutes}:${seconds}`;
    }
    if (/^\d{1,2}:\d{2}$/.test(trimmed)) {
      const [hours, minutes] = trimmed.split(':');
      return `${String(parseInt(hours, 10)).padStart(2, '0')}:${minutes}`;
    }
    return trimmed;
  };

  const handlePopupConfirm = async (isWholeSeries) => {
    try {
      setLoading(true);
      if (actionType === 'save') {
        const url = isWholeSeries ? `/ride/${selectedRideId}/series` : `/ride/${selectedRideId}`;
        const updatedFields = Object.keys(editedRide).reduce((acc, key) => {
          if (editedRide[key] !== undefined && editedRide[key] !== null) {
            acc[key] = editedRide[key];
          }
          return acc;
        }, {});

        const fallbackRide = rides.find((ride) => ride.id === selectedRideId);
        const normalizedDepart = normalizeTimeValue(updatedFields.depart || fallbackRide?.depart || '');
        await api.put(url, {
          depart: normalizedDepart,
          vehicle: updatedFields.vehicle,
          driver: updatedFields.driver,
        });
        setRides(rides.map((ride) => (ride.id === selectedRideId ? editedRide : ride)));
      } else if (actionType === 'delete') {
        const url = isWholeSeries ? `/ride/${selectedRideId}/series` : `/ride/${selectedRideId}`;
        const { data } = await api.delete(url);
        // Probe: the backend deletes nothing and returns deleted === false when
        // the ride/series has active bookings. Confirm a force delete instead.
        if (data?.deleted === false) {
          setPendingDelete({ url, rideId: selectedRideId });
          setBookings(data.bookings || []);
          setBookingWarning(data.warning || '');
          setBookingsPopup(true);
          handlePopupClose();
          return;
        }
        setRides(rides.filter((ride) => ride.id !== selectedRideId));
      }
      fetchRides();
      handlePopupClose();
      setShowEditedDepart(false);

      setEditingRow(null);
    } catch (error) {
      console.error(`Error ${actionType === 'delete' ? 'deleting' : 'updating'} ride:`, error);
    } finally {
      setLoading(false); // End loading
    }
  };

  const handleNewScheduleChange = (name, value) => {
    setNewSchedule((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const fetchRides = async () => {
    await fetchData();
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [routeRes, driversRes, vehiclesRes, ridesRes] = await Promise.all([
        api.get(`route/${routeId}`),
        api.get('/driver'),
        api.get('/vehicle'),
        api.get(`/ride?route=${routeId}`),
      ]);

      if (routeRes.data) {
        setRouteName(routeRes.data[0]?.name || 'Unknown Route');
      } else {
        navigate('/routes');
        return;
      }

      setDrivers(driversRes.data || []);
      setVehicles(vehiclesRes.data || []);

      const formattedRides = ridesRes.data.map((ride) => {
        return {
          id: ride.id,
          start: ride.depart.split(' ')[0],
          depart: ride.depart.split(' ')[1],
          vehicle: ride.vehicle,
          driver: ride.driver,
        };
      });
      setRides(formattedRides);
    } catch (error) {
      console.error('Error fetching data:', error);
      navigate('/routes');
    } finally {
      setLoading(false); // End loading
    }
  };

  useEffect(() => {
    fetchData();
  }, [routeId]);
  const handleEditClick = (ride) => {
    setEditingRow(ride.id);
    setShowEditedDepart(false);
    setEditedRide({ ...ride });
  };

  const handleSaveClick = (id) => {
    setActionType('save');
    setSelectedRideId(id);
    setOpenPopup(true);
  };

  const handleDeleteClick = (id) => {
    setActionType('delete');
    setSelectedRideId(id);
    setOpenPopup(true);
  };

  const handlePopupClose = () => {
    setOpenPopup(false);
    setSelectedRideId(null);
  };

  const handleBookingsClose = () => {
    setBookingsPopup(false);
    setPendingDelete(null);
    setBookings([]);
    setBookingWarning('');
  };

  // Commit phase: re-issue the same delete URL with force, which cancels and
  // refunds every booking before soft-deleting the ride/series.
  const handleForceDelete = async () => {
    if (!pendingDelete) return;
    try {
      setLoading(true);
      await api.delete(pendingDelete.url, { data: { force: true } });
      setRides(rides.filter((ride) => ride.id !== pendingDelete.rideId));
      fetchRides();
    } catch (error) {
      console.error('Error deleting ride:', error);
    } finally {
      setLoading(false);
      handleBookingsClose();
    }
  };

  const handleInputChange = (field, value) => {
    setEditedRide((prev) => ({ ...prev, [field]: value }));
  };

  const getDriverName = (driverId) => {
    const driver = drivers.find((d) => d.id === driverId);
    return driver ? `${driver.firstname} ${driver.lastname}` : '';
  };

  const getVehicleName = (vehicleId) => {
    const vehicle = vehicles.find((v) => v.id === vehicleId);
    return vehicle ? vehicle.name : '';
  };

  const formatDate = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hoursNum = parseInt(hours, 10);
    const ampm = hoursNum >= 12 ? 'pm' : 'am';
    const formattedHours = hoursNum % 12 || 12;
    return `${formattedHours}:${minutes} ${ampm}`;
  };

  return (
    <div style={{ padding: '20px' }}>
      <div
        style={{
          width: '100%',
          height: 'max-content',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" gutterBottom>
          <IconButton onClick={() => navigate('/routes')}>
            <ArrowLeftCircle />
          </IconButton>
          Schedule Route
        </Typography>
        <Button
          color="primary"
          size="small"
          variant="contained"
          sx={{
            padding: '7px 10px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '10px',
            fontSize: '16px',
            color: 'white',
          }}
          onClick={() => navigate(`/routes/calendar/${routeId}`)}
        >
          <Calendar size={20} /> View Calendar
        </Button>
      </div>
      <Typography variant="h4" gutterBottom>
        {routeName}
      </Typography>

      <ScheduleForm
        routeId={routeId}
        newSchedule={newSchedule}
        handleNewScheduleChange={handleNewScheduleChange}
        vehicles={vehicles}
        drivers={drivers}
        fetchRides={fetchRides}
        setNewSchedule={setNewSchedule}
      />
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Depart</TableCell>
              <TableCell>Vehicle</TableCell>
              <TableCell>Driver</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rides.map((ride) => (
              <TableRow key={ride.id}>
                <TableCell>
                  {editingRow === ride.id ? (
                    <TextField
                      type="date"
                      value={editedRide.start}
                      onChange={(e) => handleInputChange('start', e.target.value)}
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{ readOnly: true }}
                    />
                  ) : (
                    ride.start
                  )}
                </TableCell>
                <TableCell>
                  {editingRow === ride.id ? (
                    <TextField
                      label="Departure Time"
                      type="time"
                      value={showEditedDepart ? editedRide.depart : ride.depart.slice(0, 5)}
                      onChange={(e) => {
                        handleInputChange('depart', e.target.value);
                        setShowEditedDepart(true);
                      }}
                      variant="outlined"
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  ) : (
                    formatDate(ride.depart)
                  )}
                </TableCell>

                <TableCell>
                  {editingRow === ride.id ? (
                    <Select
                      value={editedRide.vehicle}
                      onChange={(e) => handleInputChange('vehicle', e.target.value)}
                      variant="outlined"
                      size="small"
                    >
                      {vehicles.map((vehicle) => (
                        <MenuItem key={vehicle.id} value={vehicle.id}>
                          {vehicle.name}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    getVehicleName(ride.vehicle)
                  )}
                </TableCell>
                <TableCell>
                  {editingRow === ride.id ? (
                    <Select
                      value={editedRide.driver}
                      onChange={(e) => handleInputChange('driver', e.target.value)}
                      variant="outlined"
                      size="small"
                    >
                      {drivers.map((driver) => (
                        <MenuItem key={driver.id} value={driver.id}>
                          {`${driver.firstname} ${driver.lastname}`}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    getDriverName(ride.driver)
                  )}
                </TableCell>
                <TableCell>
                  {editingRow === ride.id ? (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleSaveClick(ride.id)}
                      disabled={loading}
                    >
                      Save
                    </Button>
                  ) : (
                    <>
                      <IconButton color="primary" onClick={() => handleEditClick(ride)}>
                        <Edit size={18} />
                      </IconButton>
                      <IconButton style={{ color: 'red' }} onClick={() => handleDeleteClick(ride.id)}>
                        <Trash size={18} />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openPopup} onClose={handlePopupClose}>
        <DialogTitle>{actionType === 'delete' ? 'Confirm Delete' : 'Confirm Save'}</DialogTitle>
        <DialogActions>
          <Button onClick={() => handlePopupConfirm(true)} color="primary">
            Whole Series
          </Button>
          <Button onClick={() => handlePopupConfirm(false)} color="primary">
            Item Only
          </Button>
          <Button onClick={handlePopupClose} color="secondary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bookingsPopup} onClose={handleBookingsClose}>
        <DialogTitle>Ride has bookings</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {bookingWarning || `${bookings.length} passenger(s) have booked this ride.`}
          </Typography>
          {bookings.map((booking, index) => (
            <Typography key={`${booking.phone}-${index}`} variant="body2">
              {booking.firstname} {booking.lastname} — {booking.phone}
              {booking.linked_card ? ' (linked card)' : ''}
            </Typography>
          ))}
          <Typography sx={{ marginTop: '10px' }} color="error" variant="body2">
            Confirming will cancel this ride for everyone above and refund their fare.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleForceDelete} color="primary" disabled={loading}>
            Cancel &amp; Refund All
          </Button>
          <Button onClick={handleBookingsClose} color="secondary">
            Keep Ride
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ScheduleRoute;
